import styled from '@emotion/styled';
import { up } from '../../../../style-utils/media-queries';
import { gradient, layout, palette, radius, shadow, spacing, status, transition, typography } from '../../../../style-utils/theme';

/* The wizard: a rail of step names beside one step (desktop), the rail as a
   slim progress bar above the step (phone). */
export const Frame = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing.md};
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding-bottom: ${layout.fabReserve};

  ${up('lg')} {
    grid-template-columns: 256px minmax(0, 1fr);
    gap: ${spacing.lg};
  }
`;

export const Rail = styled.nav`
  display: none;

  ${up('lg')} {
    display: block;
    position: sticky;
    top: ${spacing.md};
    align-self: start;
  }
`;

export const RailList = styled.ol`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const RailButton = styled.button<{ $current: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  width: 100%;
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.sm};
  border: 0;
  border-radius: ${radius.sm};
  background: ${p => (p.$current ? palette.irisTintSoft : 'transparent')};
  color: ${p => (p.$current ? palette.ink : palette.inkSoft)};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  text-align: left;
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const RailMark = styled.span<{ $done: boolean }>`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: ${radius.circle};
  background: ${p => (p.$done ? status.success.bg : palette.surfaceMuted)};
  color: ${p => (p.$done ? status.success.fg : palette.slate)};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
`;

export const Progress = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};

  ${up('lg')} {
    display: none;
  }
`;

export const Track = styled.div`
  width: 100%;
  height: 8px;
  border-radius: ${radius.pill};
  background: ${palette.surfaceMuted};
  overflow: hidden;
`;

export const Fill = styled.div<{ $pct: number }>`
  width: ${p => p.$pct}%;
  height: 100%;
  border-radius: ${radius.pill};
  background: ${gradient.primary};
  transition: width 360ms cubic-bezier(0.16, 1, 0.3, 1);
`;

export const StepPill = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.inkSoft};
`;

export const Panel = styled.section`
  min-width: 0;
  padding: ${spacing.md} ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.sm};

  ${up('md')} {
    padding: ${spacing.lg};
  }
`;

export const StepTitle = styled.h2`
  margin: 0 0 ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const Why = styled.p`
  margin: 0 0 ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  line-height: ${typography.leading.normal};
  color: ${palette.inkSoft};
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  min-width: 0;
`;

export const Nav = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  margin-top: ${spacing.lg};
  padding-top: ${spacing.md};
  border-top: 1px solid ${palette.line};
`;

export const NavGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
`;

export const NextLine = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

export const Choices = styled.ul`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing.xs};
  margin: 0;
  padding: 0;
  list-style: none;

  ${up('md')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const Choice = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  min-width: 0;
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
`;

export const ChoiceText = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
`;

export const Quiet = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

export const Facts = styled.dl`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: ${spacing.xs} ${spacing.sm};
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;

  & dt {
    color: ${palette.slate};
  }
  & dd {
    margin: 0;
    color: ${palette.ink};
    overflow-wrap: anywhere;
  }
`;

/* The readiness checklist (on the listing and at the end of the wizard). */
export const Checklist = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const CheckItem = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  min-width: 0;
  min-height: ${layout.touchTarget};
`;

export const CheckLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
  overflow-wrap: anywhere;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
`;

export const ReadyCard = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.md};
  padding: ${spacing.md};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.sm};
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;
